import { map, shareReplay } from 'rxjs/operators';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { FixedAsset } from '../../models/entities/accounting/fixed-asset';
import { Injectable } from '@angular/core';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { Liability } from '../../models/entities/accounting/liability';
import { Equity } from '../../models/entities/accounting/equity';
import { Accounting } from '../../components/shared/http/request-interfaces';

export interface COAAccount
{
    id: number;
    label: string;
    accountType: string;
}

export interface ChartOfAccountsStreams
{
    assets: {
        $loaded: BehaviorSubject<boolean>,
        $loading: BehaviorSubject<boolean>,
        $data: BehaviorSubject<FixedAsset[]>,
        $selectable: Observable<FixedAsset[]>,
        $active: Observable<FixedAsset[]>,
        $balances: Observable<Map<number, Observable<GetBalanceResponse>>>;
    };
    liabilities: {
        $loaded: BehaviorSubject<boolean>,
        $loading: BehaviorSubject<boolean>,
        $data: BehaviorSubject<Liability[]>,
        $selectable: Observable<Liability[]>,
        $active: Observable<Liability[]>,
        $balances: Observable<Map<number, Observable<GetBalanceResponse>>>;
    };
    equities: {
        $loaded: BehaviorSubject<boolean>,
        $loading: BehaviorSubject<boolean>,
        $data: BehaviorSubject<Equity[]>,
        $selectable: Observable<Equity[]>,
        $active: Observable<Equity[]>,
        $balances: Observable<Map<number, Observable<GetBalanceResponse>>>;
    };
}

export interface GetBalanceResponse
{
    balance: number;
}

/** One display-ready row from the full Chart of Accounts export endpoint (SD-5948). */
export interface CoaExportRow
{
    section: string;
    name: string;
    labels: string;
    balance: number;
    status: string;
}

@Injectable({
    providedIn: 'root',
})
export class ChartOfAccountsService
{
    private _assets: { [orgId: number]: FixedAsset[] } = {};
    private _liabilities: { [orgId: number]: Liability[] } = {};
    private _equities: { [orgId: number]: Equity[] } = {};
    private _balances = {
        assets: new Map<number, Observable<GetBalanceResponse>>(),
        liabilities: new Map<number, Observable<GetBalanceResponse>>(),
        equities: new Map<number, Observable<GetBalanceResponse>>(),
    };

    private _orgStreams: {
        [orgId: number]: ChartOfAccountsStreams
    } = {};

    constructor(private _http: ApplicationHttpClient) {
    }

    /**
     * Clears all per-organization COA caches and streams.
     *
     * Mirrors CategoriesService.clearCache(): this provided-in-root service caches assets,
     * liabilities, equities and balances per org for the JS app lifetime. A normal org switch does
     * a full page reload (wiping it), but a router-nav logout (AuthService.logout with
     * preserveLocation) does NOT — leaving a prior org's accounts cached for the next session. The
     * category picker loads these alongside categories, so it shares the cross-org staleness
     * (SD-7271/7407/7474). Called on logout so COA accounts are always re-fetched for the active org.
     */
    public clearCache(): void {
        this._assets = {};
        this._liabilities = {};
        this._equities = {};
        this._balances = {
            assets: new Map<number, Observable<GetBalanceResponse>>(),
            liabilities: new Map<number, Observable<GetBalanceResponse>>(),
            equities: new Map<number, Observable<GetBalanceResponse>>(),
        };
        this._orgStreams = {};
    }

    getOrganizationStreams(organizationId: number) {
        if (this._orgStreams[organizationId]) {
            return this._orgStreams[organizationId];
        }

        const $assets = new BehaviorSubject<FixedAsset[]>([]);
        const $liabilities = new BehaviorSubject<Liability[]>([]);
        const $equities = new BehaviorSubject<Equity[]>([]);
        const streams = {
            assets: {
                $loaded: new BehaviorSubject<boolean>(false),
                $loading: new BehaviorSubject<boolean>(false),
                $data: $assets,
                $selectable: $assets,
                $active: $assets.pipe(map((assets) => {
                    return assets.filter((asset) => !asset.deletedAt);
                })),
                $balances: $assets.pipe(map(assets => {
                    assets.forEach(asset => {
                        if(!this._balances['assets'].has(asset.id)) {
                            const b = this.getBalance(asset)
                                    .pipe(shareReplay({bufferSize: 1, refCount: false}));
                            this._balances['assets'].set(asset.id, b);
                        }
                    });

                    return this._balances['assets'];
                }))
            },
            liabilities: {
                $loaded: new BehaviorSubject<boolean>(false),
                $loading: new BehaviorSubject<boolean>(false),
                $data: $liabilities,
                $selectable: $liabilities,
                $active: $liabilities.pipe(map((liabilities) => {
                    return liabilities.filter((liability) => !liability.deletedAt);
                })),
                $balances: $liabilities.pipe(map(liabilities => {
                    liabilities.forEach(liability => {
                        if(!this._balances['liabilities'].has(liability.id)) {
                            const b = this.getBalance(liability)
                                    .pipe(shareReplay({bufferSize: 1, refCount: false}));
                            this._balances['liabilities'].set(liability.id, b);
                        }
                    });

                    return this._balances['liabilities'];
                }))
            },
            equities: {
                $loaded: new BehaviorSubject<boolean>(false),
                $loading: new BehaviorSubject<boolean>(false),
                $data: $equities,
                $selectable: $equities.pipe(map(equities => equities.filter(equity => !equity.internalUse))),
                $active: $equities.pipe(map((equities) => {
                    return equities.filter(equity => !equity.internalUse && !equity.deletedAt);
                })),
                $balances: $equities.pipe(map(equities => {
                    equities.forEach(equity => {
                        if(!this._balances['equities'].has(equity.id)) {
                            const b = this.getBalance(equity)
                                    .pipe(shareReplay({bufferSize: 1, refCount: false}));
                            this._balances['equities'].set(equity.id, b);
                        }
                    });

                    return this._balances['equities'];
                }))
            },
        };
        this._orgStreams[organizationId] = streams;
        return streams;
    }

    public getAssets(
            organizationId: number,
    ): Observable<FixedAsset[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        organizationId = organizationId || jwt.orgId;
        const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.Accounts(organizationId, 'assets'));

        const streams = this.getOrganizationStreams(organizationId);
        streams.assets.$loading.next(true);
        return this._http.get(url).pipe(map((res: Object[]) => {
            const assets = res.map(asset => new FixedAsset(asset));
            this._assets[organizationId] = assets;
            streams.assets.$data.next(this._assets[organizationId]);
            streams.assets.$loading.next(false);
            streams.assets.$loaded.next(true);
            return assets;
        }));
    }

    public getBalance(item: Liability | FixedAsset | Equity): Observable<GetBalanceResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Organization(item.organizationId) + '/balance') + this._http.queryStringFromObject({
            accountType: item.accountType,
            accountId: item.id,
        });
        return this._http.get<GetBalanceResponse>(url);
    }

    public getLiabilities(organizationId: number): Observable<Liability[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        organizationId = organizationId || jwt.orgId;
        const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.Accounts(organizationId, 'liabilities'));

        const streams = this.getOrganizationStreams(organizationId);
        streams.liabilities.$loading.next(true);
        return this._http.get(url).pipe(map((res: Object[]) => {
            const liabilities = res.map(liability => new Liability(liability));
            this._liabilities[organizationId] = liabilities;
            streams.liabilities.$data.next(this._liabilities[organizationId]);
            streams.liabilities.$loading.next(false);
            streams.liabilities.$loaded.next(true);
            return liabilities;
        }));
    }

    public createAsset(organizationId: number, body: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Accounts(organizationId || jwt.orgId, 'assets'),
        );

        return this._http.post(url, JSON.stringify(body));
    }

    public updateAsset(organizationId: number, id: number, body: Accounting.Accounts.UpdateAccountRequest): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'assets', id),
        );

        return this._http.put(url, JSON.stringify(body));
    }

    public deleteAsset(organizationId: number, id: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'assets', id),
        );

        return this._http.delete(url);
    }

    public createLiability(organizationId: number, body: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Accounts(organizationId || jwt.orgId, 'liabilities'),
        );

        return this._http.post(url, JSON.stringify(body));
    }

    public updateLiability(organizationId: number, id: number, body: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'liabilities', id),
        );

        return this._http.put(url, JSON.stringify(body));
    }

    public deleteLiability(organizationId: number, id: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'liabilities', id),
        );

        return this._http.delete(url);
    }

    public getEquities(organizationId: number): Observable<Equity[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        organizationId = organizationId || jwt.orgId;
        const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.Accounts(organizationId, 'equities'));

        const streams = this.getOrganizationStreams(organizationId);
        streams.equities.$loading.next(true);
        return this._http.get(url).pipe(map((res: Object[]) => {
            const equities = res.map(equity => new Equity(equity));
            this._equities[organizationId] = equities;
            streams.equities.$data.next(this._equities[organizationId]);
            streams.equities.$loading.next(false);
            streams.equities.$loaded.next(true);
            return equities;
        }));
    }

    /**
     * Fetch the entire Chart of Accounts (all sections) for export (SD-5948).
     * Returns display-ready rows so a single CSV can cover every tab, including
     * tabs the user never opened (which the page renders lazily).
     */
    public exportChartOfAccounts(organizationId: number): Observable<CoaExportRow[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        organizationId = organizationId || jwt.orgId;
        const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.ChartOfAccountsExport(organizationId));

        return this._http.get<{ rows: CoaExportRow[] }>(url).pipe(map(res => res.rows));
    }

    public createEquity(organizationId: number, body: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Accounts(organizationId || jwt.orgId, 'equities'),
        );

        return this._http.post(url, JSON.stringify(body));
    }

    public updateEquity(organizationId: number, id: number, body: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'equities', id),
        );

        return this._http.put(url, JSON.stringify(body));
    }

    public deleteEquity(organizationId: number, id: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'equities', id),
        );

        return this._http.delete(url);
    }

    /**
     * Restore a soft-deleted asset.
     */
    public restoreAsset(organizationId: number, id: number): Observable<FixedAsset> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'assets', id) + '/restore',
        );

        return this._http.patch(url, JSON.stringify({})).pipe(
                map((response: Object) => new FixedAsset(response)),
        );
    }

    /**
     * Restore a soft-deleted liability.
     */
    public restoreLiability(organizationId: number, id: number): Observable<Liability> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'liabilities', id) + '/restore',
        );

        return this._http.patch(url, JSON.stringify({})).pipe(
                map((response: Object) => new Liability(response)),
        );
    }

    /**
     * Restore a soft-deleted equity.
     */
    public restoreEquity(organizationId: number, id: number): Observable<Equity> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.Account(organizationId || jwt.orgId, 'equities', id) + '/restore',
        );

        return this._http.patch(url, JSON.stringify({})).pipe(
                map((response: Object) => new Equity(response)),
        );
    }

    /**
     * Bulk delete multiple assets.
     */
    public bulkDeleteAssets(organizationId: number, ids: number[]): Observable<{ success: boolean; deletedCount: number }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.AccountingV2Module.BulkDeleteAccounts(organizationId || jwt.orgId, 'assets'),
        );

        return this._http.delete<{ success: boolean; deletedCount: number }>(url, { body: { ids } });
    }

    /**
     * Bulk delete multiple liabilities.
     */
    public bulkDeleteLiabilities(organizationId: number, ids: number[]): Observable<{ success: boolean; deletedCount: number }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.AccountingV2Module.BulkDeleteAccounts(organizationId || jwt.orgId, 'liabilities'),
        );

        return this._http.delete<{ success: boolean; deletedCount: number }>(url, { body: { ids } });
    }

    /**
     * Bulk delete multiple equities.
     */
    public bulkDeleteEquities(organizationId: number, ids: number[]): Observable<{ success: boolean; deletedCount: number }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.AccountingV2Module.BulkDeleteAccounts(organizationId || jwt.orgId, 'equities'),
        );

        return this._http.delete<{ success: boolean; deletedCount: number }>(url, { body: { ids } });
    }

    /**
     * Reorder accounts in the specified organization.
     */
    public reorderAccounts(
            organizationId: number,
            accountType: 'assets' | 'liabilities' | 'equities',
            accountIds: number[],
    ): Observable<void> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        const requestBody: Accounting.Accounts.ReorderAccountsRequest = {
            accountIds: accountIds,
        };

        return this._http.put(
                Routes.MakeLegFiCoreUrl(
                        Routes.AccountingV2Module.ReorderAccounts(organizationId, accountType),
                ),
                JSON.stringify(requestBody),
        ).pipe(map(() => void 0));
    }
}
